import { useEffect, useState } from 'react';
import { PlusCircle } from 'lucide-react';
import { v4 as uuid } from 'uuid';
import { useWizard } from '../WizardProvider';
import WizardShell from '../WizardShell';
import LosIntegrationCard from '../components/LosIntegrationCard';
import { LOS_FIELDS } from '../components/losFields';
import type { LosIntegration, LosType } from '../types';
import { STEP2_INSTRUCTION } from '../copy';

interface LosSlot {
  /** Identity for React's key, stable across re-renders — NOT the array position. Keeps each
   * card's own local state (MaskedInput's revealed, LosIntegrationCard's confirming dialog)
   * pinned to its own slot when an earlier slot is removed and later ones shift index. */
  id: string;
  type: LosType | '';
  credentials: Record<string, string>;
}

const ALL_LOS_TYPES: LosType[] = ['encompass', 'ml-consumer', 'ml-mortgage'];

const emptySlot = (): LosSlot => ({ id: uuid(), type: '', credentials: {} });

export default function Step2LosIntegrations({ onExit }: { onExit: () => void }) {
  const { state, dispatch } = useWizard();
  // Seeded lazily from the wizard state that exists at mount time. LenderWizardPage renders a
  // different component per step, so Back (or the Step 5 summary's Edit link) genuinely unmounts
  // and remounts this component — without this, it always started from [emptySlot()] and the
  // mount effect below then overwrote the reducer with that empty array, wiping any LOS the user
  // had already entered. Credentials are copied, not aliased — the reducer still owns the
  // original object. Each hydrated slot gets a fresh id (below); once seeded, the mount effect
  // just recomputes the same array it read, so there's no loop with it.
  const [slots, setSlots] = useState<LosSlot[]>(() =>
    state.losIntegrations.length
      ? state.losIntegrations.map((l) => ({ id: uuid(), type: l.type, credentials: { ...l.credentials } }))
      : [emptySlot()],
  );

  // Slots are this component's single source of truth, including the ones with no LOS chosen
  // yet (the reducer never sees those). Every change pushes the whole derived array through
  // setLosIntegrations rather than indexed addLos/removeLos, which would fall out of step
  // with the slot indices as soon as one slot is left unselected.
  useEffect(() => {
    const integrations: LosIntegration[] = slots
      .filter((s) => s.type !== '')
      .map((s) => ({ type: s.type as LosType, credentials: s.credentials }));
    dispatch({ type: 'setLosIntegrations', integrations });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots]);

  const availableFor = (index: number) => {
    const takenElsewhere = slots
      .filter((s, i) => i !== index && s.type !== '')
      .map((s) => s.type as LosType);
    return ALL_LOS_TYPES.filter((t) => !takenElsewhere.includes(t));
  };

  const handleSelect = (index: number, type: LosType) => {
    setSlots((prev) => prev.map((s, i) => (i === index ? { ...s, type, credentials: {} } : s)));
  };

  const handleChange = (index: number, patch: Record<string, string>) => {
    setSlots((prev) =>
      prev.map((s, i) => (i === index ? { ...s, credentials: { ...s.credentials, ...patch } } : s)),
    );
  };

  const handleRemove = (index: number) => {
    setSlots((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length === 0 ? [emptySlot()] : next;
    });
  };

  // A slot with no LOS chosen is an unused pick list and does not block — the step stays
  // skippable (AC11). A CHOSEN integration travels in the submit payload, so it must be
  // complete: a partial Encompass (an instance id without its credentials) integrates nothing.
  // Per-slot verdicts lifted by the cards (a taken Encompass instance id, or its check still
  // in flight — OM-3918 AC5) — the completeness rule below cannot see them.
  const [blockedSlots, setBlockedSlots] = useState<Record<string, boolean>>({});
  const setSlotBlocked = (id: string, isBlocked: boolean) =>
    setBlockedSlots((prev) => (prev[id] === isBlocked ? prev : { ...prev, [id]: isBlocked }));
  const dropSlotVerdict = (id: string) =>
    setBlockedSlots((prev) => {
      const rest = { ...prev };
      delete rest[id];
      return rest;
    });

  const canAdvance =
    slots.every(
      (slot) =>
        slot.type === '' ||
        LOS_FIELDS[slot.type].every((f) => (slot.credentials[f.key] ?? '').trim() !== ''),
    ) && !Object.values(blockedSlots).some(Boolean);

  return (
    <WizardShell title="LOS Integrations" canAdvance={canAdvance} onExit={onExit}>
      <section>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">LOS Connection</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{STEP2_INSTRUCTION}</p>
      </section>
      {slots.map((slot, index) => (
        <div key={slot.id}>
          {/* The design separates integrations with a repeated section heading, not a border. */}
          {index > 0 && (
            <h2 className="mt-8 text-sm font-semibold text-gray-700 dark:text-gray-300">LOS Connection</h2>
          )}
          <LosIntegrationCard
          index={index}
          losType={slot.type}
          credentials={slot.credentials}
          available={availableFor(index)}
          removeLabel={index === 0 ? 'Remove LOS' : 'Remove Additional LOS'}
          onSelect={(type) => handleSelect(index, type)}
          onChange={(credentials) => handleChange(index, credentials)}
          onRemove={() => {
            dropSlotVerdict(slot.id);
            handleRemove(index);
          }}
          onBlockedChange={(isBlocked) => setSlotBlocked(slot.id, isBlocked)}
        />
        </div>
      ))}
      {slots.length < 3 && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline"
            onClick={() => setSlots((prev) => [...prev, emptySlot()])}
          >
            <PlusCircle className="h-4 w-4" aria-hidden="true" /> Add Additional LOS
          </button>
        </div>
      )}
    </WizardShell>
  );
}
